import BaseDashboardCard from "../BaseDashboardCard";
import Box from "../Box";
import RatePieChart from "../RatePieChart";
import { Price } from "../../types/operational";
import { useSearchParams } from "react-router-dom";
import { SingleValue } from "react-select";
import { Choice } from "../../types/commercial";
import styled from "styled-components";
import SelectOption from "../SelectOption";
import { CText } from "@carrefour/design-system-components-react";
import { useOptionClosingRate } from "../../utils/options";
import { eventTrack } from "../../utils/gtag";
import { useTranslation } from "react-i18next";
import { formatPercentage } from "../../utils/numberUtils";
import { TFunction } from "i18next";
import { useRange } from "../../context/rangeContext";
import { Range } from "../../utils/range";

const BoxCustum = styled(Box)`
  width: 60%;
  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`;
const BoxGaping = styled(Box)<{ gap: string }>`
  gap: ${(props) => props.gap};
`;
const Text = styled(CText)`
  font-family: system-ui;
`;
type ClosingRateCardProps = {
  isUnavailable: boolean;
  isLoading: boolean;
  title: string;
  data: Price[];
  range: string;
  options: Choice[];
  openInformationModal: (title?: string) => void;
};


const formatSecteur = (secteur: string, t: TFunction) => {
  switch (secteur) {
    case "1 - PGC":
      return t('commercial.pgc');
    case "2 - PFT":
      return t('commercial.pft');
    case "3 - BAZAR":
      return t('commercial.bazar');
    case "4 - EPCS":
      return t('commercial.epcs');
    case "6 - TEXTILE":
      return t('commercial.textile');
    case "7 - NONAL":
      return t('commercial.non_al');
    default:
      return secteur;
  }
};
const ClosingRateCard = ({
  data,
  range,
  options,
  title,
  ...rest
}: ClosingRateCardProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setKpiRange, getKpiRange } = useRange();

  const getDefaultValueParams = () => {
    searchParams.set(range, getKpiRange(range) ?? options?.[0]?.value as string);
    setSearchParams(searchParams);
    // tracking first
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: options?.[0]?.value,
    });
    return options?.[0]?.value;
  };

  const RangeParam = searchParams.get(range) ?? getDefaultValueParams();
  const handleChange = (choice: SingleValue<Choice>) => {
    const newValue = choice?.value as string;

    if (newValue === RangeParam) {
      return;
    }
    searchParams.set(range, newValue as string);
    setSearchParams(searchParams);
    setKpiRange(range, newValue as Range);
    // tracking changed
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: newValue,
    });
  };
  const closingTotalPercentage =
    data
      ?.map((e) => e.closureRate)
      ?.reduce((a: number, b: number) => a + b, 0) / data?.length;
      const optionClosingRate = useOptionClosingRate();
      const {t}=useTranslation();

  return (
    <BaseDashboardCard
      hasModal={true}
      title={title}
      size="small"
      importedDateTime={data?.[0]?.importedDateTime}
      option={
          <BoxCustum py={1}>
            <SelectOption
              list={optionClosingRate}
              value={RangeParam}
              handleChange={handleChange}
            />
          </BoxCustum>}
      {...rest}
    >
      <BoxGaping
        gap={"24px"}
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
      >
        <RatePieChart auditPercentage={closingTotalPercentage} />
        {subItem(data, t)}
      </BoxGaping>
    </BaseDashboardCard>
  );
};

export default ClosingRateCard;
const subItem = (dataList: Price[], t: TFunction) => {
  return (
    <Box
      width={"100%"}
      display={"flex"}
      justifyContent={"space-around"}
      pb={2}
      data-testid={"subItem"}
    >
      {dataList?.map((item) => (
        <BoxGaping
          gap={"8px"}
          key={item?.sector}
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
        >
          <Text bold>{formatSecteur(item?.sector, t)}</Text>
          <Text size={"s"}>{`${formatPercentage(item?.closureRate)}`}</Text>
        </BoxGaping>
      ))}
    </Box>
  );
};